import {
  DAY_NAMES_LONG,
  DAY_NAMES_SHORT,
  DAYS_PER_WEEK,
  HOURS_PER_HALF_DAY,
  MONTH_NAMES_SHORT,
  NOON_HOUR,
} from "./VaultMeetingsDialog.constants";
import {
  IVaultMeeting,
  IVaultMeetingCounts,
  IVaultMeetingFilters,
} from "./VaultMeetingsDialog.types";

const MINUTE_PADDING = 2;

export const addDays = (date: Date, dayCount: number): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + dayCount);

export const getWeekStart = (date: Date): Date => addDays(date, -date.getDay());

export const isSameDay = (first: Date, second: Date): boolean =>
  first.getFullYear() === second.getFullYear() &&
  first.getMonth() === second.getMonth() &&
  first.getDate() === second.getDate();

export const getWeekDays = (weekStart: Date): Date[] =>
  Array.from({ length: DAYS_PER_WEEK }, (_, index) =>
    addDays(weekStart, index),
  );

export const getWeekRangeLabel = (weekStart: Date): string => {
  const weekEnd = addDays(weekStart, DAYS_PER_WEEK - 1);
  const startText = `${MONTH_NAMES_SHORT[weekStart.getMonth()]} ${weekStart.getDate()}`;
  const endMonthText =
    weekStart.getMonth() === weekEnd.getMonth()
      ? ""
      : `${MONTH_NAMES_SHORT[weekEnd.getMonth()]} `;
  return `${startText} – ${endMonthText}${weekEnd.getDate()}, ${weekEnd.getFullYear()}`;
};

export const getShortDayName = (date: Date): string =>
  DAY_NAMES_SHORT[date.getDay()];

export const getDayDateLabel = (date: Date): string =>
  `${MONTH_NAMES_SHORT[date.getMonth()]} ${date.getDate()}`;

export const getLongDayLabel = (date: Date): string =>
  `${DAY_NAMES_LONG[date.getDay()]}, ${getDayDateLabel(date)}`;

export const formatClockTime = (date: Date): string => {
  const hours = date.getHours();
  const displayHours = hours % HOURS_PER_HALF_DAY || HOURS_PER_HALF_DAY;
  const minutes = String(date.getMinutes()).padStart(MINUTE_PADDING, "0");
  const suffix = hours < NOON_HOUR ? "am" : "pm";
  return `${displayHours}:${minutes} ${suffix}`;
};

export const formatMeetingTimeRange = (meeting: IVaultMeeting): string =>
  `${formatClockTime(meeting.start)} - ${formatClockTime(meeting.end)}`;

export const getMeetingsForWeek = (
  meetings: IVaultMeeting[],
  weekStart: Date,
): IVaultMeeting[] => {
  const weekEnd = addDays(weekStart, DAYS_PER_WEEK);
  return meetings.filter(({ start }) => start >= weekStart && start < weekEnd);
};

export const getMeetingsForDay = (
  meetings: IVaultMeeting[],
  day: Date,
): IVaultMeeting[] =>
  meetings
    .filter(({ start }) => isSameDay(start, day))
    .sort((first, second) => first.start.getTime() - second.start.getTime());

export const filterMeetings = (
  meetings: IVaultMeeting[],
  { titleText, transcriptStatus }: IVaultMeetingFilters,
): IVaultMeeting[] => {
  const normalizedText = titleText.trim().toLowerCase();
  return meetings.filter(
    ({ title, hasTranscript }) =>
      title.toLowerCase().includes(normalizedText) &&
      (transcriptStatus === "any" ||
        (transcriptStatus === "with") === hasTranscript),
  );
};

export const countActiveFilters = ({
  titleText,
  transcriptStatus,
}: IVaultMeetingFilters): number =>
  (titleText.trim() ? 1 : 0) + (transcriptStatus !== "any" ? 1 : 0);

export const countMeetings = (
  meetings: IVaultMeeting[],
): IVaultMeetingCounts => ({
  total: meetings.length,
  withTranscript: meetings.filter(({ hasTranscript }) => hasTranscript).length,
});

export const formatMeetingSummary = ({
  total,
  withTranscript,
}: IVaultMeetingCounts): string => {
  const meetingWord = total === 1 ? "meeting" : "meetings";
  const transcriptWord = withTranscript === 1 ? "transcript" : "transcripts";
  return `${total} ${meetingWord} this week · ${withTranscript} with ${transcriptWord}`;
};
